/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  isAddNewNgX2ConnectionSelector,
  ngX2ConnectionListSelector,
  ngX2ConnectionIndexesSelector,
} from "../../../../store/api/deviceConfig/selectors";
import { IngpeerInterfaces } from "../../../../store/api/deviceConfig/types";
import { getNextNgX2ConnectionIndex, prepareNgX2ConnectionPayload } from "../../helpers";
import { selectedDeviceSelector } from "../../../../store/api/devices/selectors";
import { OPERATIONTYPES } from "../../../../globals/constants";
import { useSetDeviceConfigMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../../store/hooks";
import { setOpenCustomDailog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { setNgX2ConnectionList } from "../../../../store/api/deviceConfig/reducers";
import { initialState } from "../../../../store/api/deviceConfig/initialState";
import { errorMessage } from "../../../../utils/helpers";
import { isIPv4, isIPv6 } from "../../../../utils/validations";

interface IPayload {
  [key: string]: string;
}

export const useNgX2ConnectionData = (selectedNgX2Connection?: IngpeerInterfaces) => {
  const dispatch = useStoreDispatch();
  const [NgX2ConnectionData, setNgX2ConnectionData] = useState(selectedNgX2Connection);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [anchorElNgX2Connection, setAnchorElNgX2Connection] = useState<null | HTMLElement>(null);

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  let NgX2ConnectionList: IngpeerInterfaces[] = useSelector(ngX2ConnectionListSelector);
  const isAddNewNgX2Connection = useSelector(isAddNewNgX2ConnectionSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const NgX2ConnectionIndexes = useSelector(ngX2ConnectionIndexesSelector);
  const nextNgX2ConnectionIndex = getNextNgX2ConnectionIndex(NgX2ConnectionIndexes, 10);

  useEffect(() => {
    if (isAddNewNgX2Connection) {
      setNgX2ConnectionData({
        ...initialState.selectedNgX2Connection,
        index: nextNgX2ConnectionIndex!,
      });
    } else {
      setNgX2ConnectionData(selectedNgX2Connection);
    }
  }, [isAddNewNgX2Connection, nextNgX2ConnectionIndex, selectedNgX2Connection]);

  const onblureHandler = (event: any) => {
    const { name, value } = event.target;
    if (name === "index") {
      if (NgX2ConnectionIndexes?.includes(NgX2ConnectionData?.index)) {
        setErrMsg({ ...errMsg, index: `Index ${NgX2ConnectionData?.index} is already in use.` });
        return;
      }
      if (!!NgX2ConnectionData?.index && Number(NgX2ConnectionData?.index) > 32) {
        setErrMsg({ ...errMsg, index: `Index limit range is 1-32.` });
        return;
      }
    }
    if (name === "address" && !!value) {
      const isValid = isIPv4(value) || isIPv6(value);
      !isValid && setErrMsg({ ...errMsg, [name]: `Invalid IP Address` });
    }
    if (nextNgX2ConnectionIndex !== NgX2ConnectionData?.index) {
      setNgX2ConnectionData({ ...NgX2ConnectionData! });
    }
  };

  const submitHandler = async () => {
    if (!!NgX2ConnectionData) {
      if (Object.keys(NgX2ConnectionData).length <= 0) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please fill the required fields!" }));
        return;
      }
      //onblureHandler();
      // const { errorMessages, isValid } = validate(NgX2ConnectionData);
      // setErrMsg({ ...errMsg, ...errorMessages });

      const operationType = isAddNewNgX2Connection ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
      const NgX2ConnectionPayload =
        !!NgX2ConnectionData &&
        !!selectedDevice?.objectKey &&
        prepareNgX2ConnectionPayload(NgX2ConnectionData, selectedNgX2Connection, selectedDevice?.objectKey, operationType);
      if (!!NgX2ConnectionData) {
        NgX2ConnectionList = [NgX2ConnectionData, ...NgX2ConnectionList];
      }
      try {
        let result: any;
        dispatch(setNgX2ConnectionList(NgX2ConnectionList));
        dispatch(setOpenCustomDailog(false));
        setAnchorElNgX2Connection(null);

        result = await setDeviceConfig(NgX2ConnectionPayload);
        if (result?.error || configStatus?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been submited`,
            })
          );
        }
      } catch (error: any) {
        let lastAdded = NgX2ConnectionList.filter((item) => item.index === NgX2ConnectionData.index)[0];
        lastAdded = { ...lastAdded };
        const updateToNgX2ConnectionList = NgX2ConnectionList.filter((item) => item.index !== NgX2ConnectionData.index);
        dispatch(setNgX2ConnectionList([lastAdded, ...updateToNgX2ConnectionList]));
        setTimeout(() => {
          const removedFailed = NgX2ConnectionList.filter((item) => item.index !== NgX2ConnectionData.index);
          dispatch(setNgX2ConnectionList(removedFailed));
        }, 5000);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    NgX2ConnectionData,
    setNgX2ConnectionData,
    isAddNewNgX2Connection,
    submitHandler,
    onblureHandler,
    errMsg,
    setErrMsg,
    anchorElNgX2Connection,
    setAnchorElNgX2Connection,
  };
};
